import { ClusterEditPage } from 'src/app/cluster/cluster-edit/cluster-edit.page';
import {
  Component, Input, OnDestroy, OnInit, ViewChild, inject,
} from '@angular/core';
import { FormMenuItems } from 'src/app/@lib/types';
import { ModalController, ModalOptions } from '@ionic/angular';
import { OrganizationEditPage } from 'src/app/organization/organization-edit/organization-edit.page';
import { PersonEditPage } from 'src/app/person/person-edit/person-edit.page';
import { Subject } from 'rxjs';
import { TopicEditPage } from 'src/app/topic/topic-edit/topic-edit.page';
import { UserEditPage } from 'src/app/user/user-edit/user-edit.page';
import { UserService } from 'src/app/user/@shared/user.service';
import { getIconByFormType } from 'src/app/@shared/form/form-helper';
import { takeUntil } from 'rxjs/operators';
import { transformToUpperSnakeCase } from 'src/app/@shared/utils/to-upper-snake-case';

const AUTHORIZED_EDITOR_ROLES = ['editor', 'admin', 'superadmin'];

@Component({
  selector: 'new-dataset-button',
  standalone: false,
  styleUrls: ['./new-dataset-button.component.scss'],
  template: `
    @if (currentUserIsAuthorizedEditor) {
      <ion-button class="proto-button" fill="outline" color="primary"
        (click)="showMenu($event)"
        [disabled]="isDisabled">
        <ion-icon name="add-circle-outline"></ion-icon>
        {{ 'NEW_DATASET_MENU.BUTTON' | translate }}
      </ion-button>
      <ion-popover #popover mode="md"
        [isOpen]="menuIsActive"
        [dismissOnSelect]="true"
        (didDismiss)="menuIsActive = false">
        <ng-template>
          <ion-content>
            <ion-list>
              @for (item of menuItems; track item.id) {
                <ion-item [button]="true" [detail]="false" (click)="onMenuItemClicked(item.id)">
                  <ion-icon color="primary" [name]="item.icon" slot="start"></ion-icon>
                  <ion-label>{{ item.label | translate }}</ion-label>
                </ion-item>
              }
            </ion-list>
          </ion-content>
        </ng-template>
      </ion-popover>
    }
  `,
})
export class NewDatasetButtonComponent implements OnDestroy, OnInit {
  @ViewChild('popover') popover!: HTMLIonPopoverElement;

  @Input() public isDisabled: boolean;

  public currentUserIsAuthorizedEditor = false;
  public menuIsActive = false;
  public menuItems: { id: FormMenuItems; label: string; icon: string }[] = [];

  private menuIds: FormMenuItems[] = [
    'selfHelpAddress',
    'otherAddress',
    'person',
    'topic',
    'user',
    'cluster',
  ];

  private modalController = inject(ModalController);
  private ngUnsubscribe = new Subject<void>();
  private userService = inject(UserService);

  public ngOnInit(): void {
    this.menuItems = this.menuIds.map((id) => {
      return {
        icon: getIconByFormType(id),
        id,
        label: `NEW_DATASET_MENU.LIST.${transformToUpperSnakeCase(id)}.LABEL`,
      };
    });

    this.userService.getCurrentUser()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((user) => {
        this.currentUserIsAuthorizedEditor = !!user?.roles?.some((role) => {
          return AUTHORIZED_EDITOR_ROLES.includes(role.slug);
        });

        if (!this.currentUserIsAuthorizedEditor) {
          this.menuIsActive = false;
        }
      });
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public showMenu(e: Event): void {
    if (!this.currentUserIsAuthorizedEditor) {
      return;
    }

    this.popover.event = e;
    this.menuIsActive = true;
  }

  public async onMenuItemClicked(type: FormMenuItems): Promise<{ data: any; role: string } | null> {
    if (!this.currentUserIsAuthorizedEditor) {
      return null;
    }

    const options: ModalOptions = this.getModalOptions(type);

    if (!options) {
      return null;
    }

    const topModal = await this.modalController.getTop();

    options.presentingElement = topModal || document.querySelector('ion-router-outlet');
    options.backdropDismiss = false; // prevent backdrop click to close modal (customer request)

    const modal = await this.modalController.create(options);

    // `@Input() public presentingElement` in modal page
    options.componentProps.presentingElement = modal;

    await modal.present();

    const { data, role } = await modal.onWillDismiss();

    return Promise.resolve({
      data,
      role,
    });
  }

  // eslint-disable-next-line class-methods-use-this, max-lines-per-function
  private getModalOptions(type: FormMenuItems): ModalOptions | null {
    const addressComponentProps = {
      isModal: true,
      item: {
        type,
      },
    };

    const generalComponentProps = {
      item: {
        type,
      },
      presentingElement: null,
    };

    switch (type) {
      case 'selfHelpAddress':
      case 'otherAddress': // eslint-disable-line padding-line-between-statements
        return {
          component: OrganizationEditPage,
          componentProps: addressComponentProps,
          cssClass: 'proto-modal',
        };

      case 'person':
        return {
          component: PersonEditPage,
          componentProps: generalComponentProps,
          cssClass: 'proto-modal',
        };

      case 'topic':
        return {
          component: TopicEditPage,
          componentProps: generalComponentProps,
          cssClass: 'proto-modal',
        };

      case 'user':
        return {
          component: UserEditPage,
          componentProps: generalComponentProps,
          cssClass: 'proto-modal',
        };

      case 'cluster':
        return {
          component: ClusterEditPage,
          componentProps: generalComponentProps,
          cssClass: 'proto-modal',
        };

      default:
        console.error('There are no modal options defined for this type:', type);

        return null;
    }
  }
}
